import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ApplicationInfo from './pages/ApplicationInfo';
import ApplicationForm from './pages/ApplicationForm';
import Careers from './pages/Careers';
import CareerApply from './pages/CareerApply';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ApprovalLogin from './pages/ApprovalLogin';
import ApprovalDashboard from './pages/ApprovalDashboard';
import './App.css';

function AppContent() {
  const [adminUser, setAdminUser] = useState(null);
  const [approvalUser, setApprovalUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/check-auth').then(res => res.json()),
      fetch('/api/approval/check-auth').then(res => res.json())
    ])
      .then(([adminData, approvalData]) => {
        if (adminData.authenticated) setAdminUser(adminData.user);
        if (approvalData.authenticated) setApprovalUser(approvalData.user);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = useCallback(() => {
    fetch('/api/admin/logout', { method: 'POST' }).catch(() => {});
    setAdminUser(null);
  }, []);

  const handleLogin = useCallback(() => {
    fetch('/api/admin/check-auth')
      .then(res => res.json())
      .then(data => {
        if (data.authenticated) setAdminUser(data.user);
      });
  }, []);

  const handleApprovalLogin = useCallback((user) => {
    setApprovalUser(user);
  }, []);

  const handleApprovalLogout = useCallback(() => {
    fetch('/api/approval/logout', { method: 'POST' }).catch(() => {});
    setApprovalUser(null);
  }, []);

  const isAdmin = !!adminUser;
  const isApprovalUser = !!approvalUser;

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/" element={<ApplicationInfo />} />
        <Route path="/apply" element={<ApplicationForm />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/apply" element={<CareerApply />} />
        <Route
          path="/admin/login"
          element={!isAdmin ? <AdminLogin onLogin={handleLogin} /> : <Navigate to="/admin/dashboard" />}
        />
        <Route
          path="/admin/dashboard"
          element={isAdmin ? <AdminDashboard adminUser={adminUser} onLogout={handleLogout} /> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/approval"
          element={isApprovalUser ? <Navigate to="/approval/dashboard" /> : <Navigate to="/approval/login" />}
        />
        <Route
          path="/approval/login"
          element={!isApprovalUser ? <ApprovalLogin onLogin={handleApprovalLogin} /> : <Navigate to="/approval/dashboard" />}
        />
        <Route
          path="/approval/dashboard"
          element={isApprovalUser ? <ApprovalDashboard user={approvalUser} onLogout={handleApprovalLogout} /> : <Navigate to="/approval/login" />}
        />
        <Route
          path="/approval/requests/:id"
          element={isApprovalUser ? <ApprovalDashboard user={approvalUser} onLogout={handleApprovalLogout} /> : <Navigate to="/approval/login" />}
        />
      </Routes>
    </Router>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    console.error('REACT_APP_GOOGLE_CLIENT_ID is not set in environment variables');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId || ''}>
      <AppContent />
    </GoogleOAuthProvider>
  );
}

export default App;
